import { ProtectedRoute } from '@app/Auth/ProtectedRoute'
import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { FEATURE_FLAGS } from '@app/app-constants'
import { RiskLandingPage } from '@app/components/Risk/Overview/RiskLandingPage'
import RiskLayout from '@app/components/Risk/layout/RiskLayout'
import { appLink } from '@app/config/appLink'
import { UnauthorizedPage } from '@app/routes/UnauthorisedPage/UnauthorizedPage'
import { Navigate, Outlet, Route } from 'react-router-dom'

import {
  AdaptationLibraryDrawer,
  AdaptationLibraryPage,
  PrioritisationLandingPage,
  PrioritisationPage,
  ProfileAnalysisPage,
  RiskPage,
  RiskProfilePage,
  RiskStoryDetail,
  RiskStoryList,
  SelectedElementAnalysisPage,
  StoryBuilderContent,
  StoryBuilderPanel,
  SummaryPage,
} from '@app/modules/Risk/route'

import { withSuspense } from '@app/lib/withSuspense'
import {
  CreateEditWorkspace,
  SavedWorkspacesPage,
  WorkspaceEdit,
  WorkspacesCreate,
  WorkspacesDialog,
  WorkspacesManager,
} from '@app/routes/lazyWorkspaces'

export default function RiskRoutes() {
  return (
    <Route path="risk">
      <Route index element={<Navigate to="overview" replace />} />

      <Route
        path="overview"
        element={
          <ProtectedRoute>
            <RiskLayout>
              <RiskLandingPage />
            </RiskLayout>
          </ProtectedRoute>
        }
      />

      <Route path="profile">
        <Route
          index
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>{withSuspense(<RiskProfilePage />)}</RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />

        <Route
          path="analysis"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>{withSuspense(<ProfileAnalysisPage />)}</RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />

        {/* Selected Element */}
        <Route
          path="element/:assetType/:aggregateId"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>{withSuspense(<SelectedElementAnalysisPage />)}</RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
      </Route>

      {/* Workspaces V2 */}
      <Route
        path="workspaces"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <SavedWorkspacesPage />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      <Route
        path="workspaces/create"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <CreateEditWorkspace />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      <Route
        path="workspaces/:workspaceId/edit"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <CreateEditWorkspace />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />

      <Route
        path="explorer"
        element={
          <ProtectedRoute>
            <RiskLayout>{withSuspense(<RiskPage />)}</RiskLayout>
          </ProtectedRoute>
        }
      >
        {/* Legacy workspaces. Leaving intact for now. */}
        <Route path="workspaces" element={withSuspense(<WorkspacesDialog />)}>
          <Route path="saved" element={withSuspense(<WorkspacesManager />)} />
          <Route path="create" element={withSuspense(<WorkspacesCreate />)} />
          <Route path=":workspaceId/edit" element={withSuspense(<WorkspaceEdit />)} />
        </Route>

        <Route path="story-builder" element={withSuspense(<StoryBuilderPanel />)}>
          <Route index element={withSuspense(<StoryBuilderContent />)} />
          <Route path=":storyId" element={withSuspense(<StoryBuilderContent />)} />
        </Route>
      </Route>

      <Route
        path="summary"
        element={
          <ProtectedRoute>
            <RiskLayout>{withSuspense(<SummaryPage />)}</RiskLayout>
          </ProtectedRoute>
        }
      />

      <Route path="prioritisation">
        <Route
          index
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.PRIORITISATION_PAGE}
              fallback={<Navigate to={appLink.homeWelcome.path} />}
            >
              <ProtectedRoute>
                <RiskLayout>{withSuspense(<PrioritisationLandingPage />)}</RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />

        <Route
          path="start"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.PRIORITISATION_PAGE}
              fallback={<Navigate to={appLink.homeWelcome.path} />}
            >
              <ProtectedRoute>
                <RiskLayout>{withSuspense(<PrioritisationPage />)}</RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
      </Route>

      <Route path="stories">
        <Route
          index
          element={
            <ProtectedRoute>
              <RiskLayout>{withSuspense(<RiskStoryList />)}</RiskLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path=":storyId/:slug"
          element={
            <ProtectedRoute redirectTo="./view">
              <RiskLayout>{withSuspense(<RiskStoryDetail />)}</RiskLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="adaptation-library"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.ADAPTATION_LIBRARY}
            fallback={<Navigate to={appLink.homeWelcome.path} />}
          >
            <ProtectedRoute>
              <RiskLayout>
                {withSuspense(<AdaptationLibraryPage />)}
                <Outlet />
              </RiskLayout>
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      >
        <Route path=":adaptationId" element={withSuspense(<AdaptationLibraryDrawer />)} />
      </Route>
    </Route>
  )
}
